import { apiClient } from '../client';
import { PurchaseOrderDetails } from '../../../contract/dtos/purchaseRequest';

export interface DraftPurchaseOrder {
  id: number;
  total_quantity: number;
  number_of_products: number;
  items: Array<{
    quantity: number;
    price: {
      price: number;
      currency: string;
    };
    price_per_unit: {
      price: number;
      currency: string;
    };
    tenant_product: {
      id: number;
      product_id: number;
      tenant_product_name: string;
      tenant_product_sku: string;
      tenant_product_prices: Array<{
        price: number;
        currency: string;
      }>;
      minimum_quantity: number;
      maximum_quantity: number;
      price_assigned: boolean;
      customer_view: boolean;
      brand: string;
      brand_url: string;
      country_of_origin: string;
      currently_assigned: boolean;
      count_in_branch_pr: any;
      catalog_info: {
        id: number;
        name: string;
        sku: string;
        status: string;
        description: string;
        short_name: string | null;
        short_description: string | null;
        master_product_id: number;
        is_assigned: boolean;
        price: number;
        variants_count: number;
        product_images: Array<{
          product_id: number;
          image_path: string;
        }>;
        product_categories: Array<{
          product_id: number | null;
          category: {
            id: number;
            name: string;
            image: string;
            level: number;
            is_main: boolean | null;
            parent_category: any;
          };
          is_main: boolean;
        }>;
        product_brands: any;
        feature_values: Array<{
          feature_id: number;
          feature_Name: string;
          product_id: number;
          featureVariant: {
            variant_id: number;
            variant_name: string;
            image_path: string | null;
          };
        }>;
      };
    };
  }>;
}

export const submitPurchaseOrder = async (
  componentId: string,
  tenantId: number,
  branchId: number,
  body: {
    branch_id: number;
    payment_method_id: number;
    additional_notes: string;
  }
) => {
  try {

    await apiClient.post(
      `/corporate/v1/tenant/${tenantId}/po/submit?branch_id=${branchId}`,
      body,
      {
        params: {
          component_id: componentId
        }
      }
    );
  } catch (error: any) {
    return {
      errorCode: error.response?.data?.exceptionResponse?.errorCode,
      errorMessage: error.response?.data?.exceptionResponse?.errorMessage || error.response?.data?.message,
    };
  }
};

export const updatePurchaseOrderItem = async (
  componentId: string,
  tenantId: number,
  body: {
    purchase_requests_ids: number[];
  }
) => {
  try {
    await apiClient.post(
      `/corporate/v1/tenant/${tenantId}/po`,
      body,
      {
        params: {
          component_id: componentId
        }
      }
    );
    // Return success data if needed
    return { success: true };
  } catch (error: any) {
    // Throw the error so React Query can catch it
    throw {
      errorCode: error.response?.data?.exceptionResponse?.errorCode,
      errorMessage: error.response?.data?.exceptionResponse?.errorMessage || error.response?.data?.message,
    };
  }
};

export const getCurrentDraftPO = async (tenantId: number, componentId: string): Promise<DraftPurchaseOrder | { errorCode: string; errorMessage: string }> => {
  try {
    const response = await apiClient.get(
      `/corporate/v1/tenant/${tenantId}/po`,
      {
        params: {
          component_id: componentId
        }
      }
    );

    return response.data;
  } catch (error: any) {
    return {
      errorCode: error.response?.data?.exceptionResponse?.errorCode,
      errorMessage: error.response?.data?.exceptionResponse?.errorMessage || error.response?.data?.message,
    };
  }
};

export const deletePurchaseOrderItem = async (
  componentId: string,
  tenantId: number,
  body: {
    tenant_product_id: number;
  }
) => {
  try {
    await apiClient.delete(
      `/corporate/v1/tenant/${tenantId}/po/items`,
      {
        params: {
          component_id: componentId
        },
        data: body
      }
    );
  } catch (error: any) {
    return {
      errorCode: error.response?.data?.exceptionResponse?.errorCode,
      errorMessage: error.response?.data?.exceptionResponse?.errorMessage || error.response?.data?.message,
    };
  }
};

export interface PurchaseOrderListItem {
  id: number;
  total_quantity: number;
  number_of_products: number;
  ordered_by: string | null;
  additional_notes: string | null;
  created_at: string;
  created_by: string;
  status: string;
  number_of_purchase_requests: number;
  order_summary: {
    subtotal: number | null;
    included_discount: number;
    subtotal_discount: number | null;
    shipping_fees: number | null;
    VAT: number | null;
    total: number | null;
    VAT_percentage: number | null;
  } | null;
  branch: {
    id: number;
    name: string;
    created_at: string;
    address: {
      id: number;
      street: string;
      building_number: string;
      postal_code: string;
      additional_number: string | null;
      country: {
        id: number;
        name: string;
        mobile_code: string;
      };
      city: {
        id: number;
        name: string;
      };
      area: {
        id: number;
        name: string;
      };
    };
  } | null;
}

export const getPurchaseOrderById = async (
  componentId: string,
  tenantId: string,
  poId: string
): Promise<{ data: PurchaseOrderDetails, headers: any } | { errorCode: string; errorMessage: string }> => {
  try {
    const response = await apiClient.get(
      `/corporate/v1/po/${poId}`,
      {
        params: {
          component_id: componentId,
          tenant_id: tenantId
        }
      }
    );

    return {
      data: response.data,
      headers: response.headers
    };
  } catch (error: any) {
    return {
      errorCode: error.response?.data?.exceptionResponse?.errorCode,
      errorMessage: error.response?.data?.exceptionResponse?.errorMessage || error.response?.data?.message,
    };
  }
};

export const getPurchaseOrdersList = async (
  componentId: string,
  tenantId: string,
  branchesIds: string,
  sort?: string,
  page?: number,
  size?: number
): Promise<{ data: PurchaseOrderListItem[], headers: any } | { errorCode: string; errorMessage: string }> => {
  try {
    const params = new URLSearchParams();
    params.append('component_id', componentId);
    params.append('tenant_id', tenantId);
    params.append('branches_ids', branchesIds);
    if (page) {
      params.append('page', page?.toString() || '0');
    }
    if (size) {
      params.append('size', size.toString());
    }
    if (sort) {
      params.append('sort', sort);
    }
    const response = await apiClient.get(
      `/corporate/v1/po?${params.toString()}`
    );

    return {
      data: response.data,
      headers: response.headers
    };
  } catch (error: any) {
    return {
      errorCode: error.response?.data?.exceptionResponse?.errorCode,
      errorMessage: error.response?.data?.exceptionResponse?.errorMessage || error.response?.data?.message,
    };
  }
};

export const updatePurchaseOrderItemQuantity = async (
  componentId: string,
  tenantId: number,
  body: {
    tenant_product_id: number;
    quantity: number;
  }
) => {
  try {
    await apiClient.put(
      `/corporate/v1/tenant/${tenantId}/po/items/quantity`,
      body,
      {
        params: {
          component_id: componentId
        }
      }
    );
  } catch (error: any) {
    return {
      errorCode: error.response?.data?.exceptionResponse?.errorCode,
      errorMessage: error.response?.data?.exceptionResponse?.errorMessage || error.response?.data?.message,
    };
  }
};

export const setPurchaseOrderBranch = async (
  tenantId: string,
  componentId: string,
  branchId: string
) => {
  try {
    const response = await apiClient.put(
      `/corporate/v1/tenant/${tenantId}/po/branch`,
      {
        branch_id: branchId
      },
      {
        params: {
          component_id: componentId
        }
      }
    );
    return response.data;
  } catch (error: any) {
    return {
      errorCode: error.response?.data?.exceptionResponse?.errorCode,
      errorMessage: error.response?.data?.exceptionResponse?.errorMessage || error.response?.data?.message,
    };
  }
};

